import { ApiError, apiErrorCode } from "@/lib/api";
import {
  hasCapability,
  type AuthedRequester,
  type BrokerIdentity,
} from "@/lib/brokerAuth";
import { formatShortDate } from "@/lib/listFormat";
import { readSaveError } from "@/base/wizard/saveError";
import type {
  BrokerPaymentPlan,
  BrokerQuote,
} from "@/lib/submissions/brokerSubmissions";
import type { BillingElection } from "@/lib/submissions/coverageTerms";

// Select & bind (PMS-S554, PMS-S555; BRK-S209). The broker chooses a quote
// with its billing elections, then asks Signal to bind it: no carrier
// binds through an API yet (D12), so every bind is a request in Signal's
// queue (202), never a binding here. Every state below is read from PMS's
// answer (NN #7): a request never reads as bound, and the portal never asks
// for a card or bank number, only the method's type.

const BASE = "/api/broker/submissions";

export type BillingType = NonNullable<BillingElection["billingType"]>;
export const BILLING_TYPES: readonly BillingType[] = [
  "direct_bill",
  "agency_bill",
];

export type PaymentMethod = NonNullable<BillingElection["paymentMethod"]>;
export const PAYMENT_METHODS: readonly PaymentMethod[] = [
  "bank_draft",
  "echeck",
  "credit_card",
  "debit_card",
  "check",
  "wire",
];

export type PaymentPlan = NonNullable<BillingElection["paymentPlan"]>;
export const PAYMENT_PLANS: readonly PaymentPlan[] = [
  "annual",
  "quarterly",
  "monthly",
  "pay_as_you_go",
  "semi_annual",
  "percent_down",
  "premium_financed",
  "other",
];

export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  // "Direct bill", the trade's word and PMS's (PMS-S546; BRK-S510).
  direct_bill: "Direct bill",
  agency_bill: "Agency bill",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_draft: "Bank draft",
  echeck: "eCheck",
  credit_card: "Credit card",
  debit_card: "Debit card",
  check: "Check",
  wire: "Wire transfer",
};

export const PAYMENT_PLAN_LABELS: Record<PaymentPlan, string> = {
  annual: "Pay in full",
  quarterly: "Quarterly",
  monthly: "Monthly",
  pay_as_you_go: "Pay as you go",
  semi_annual: "Twice a year",
  percent_down: "Percent down",
  premium_financed: "Premium financed",
  other: "Other",
};

/** A partial election: what's sent replaces the quote's, the rest stays. */
export interface BillingElectionPatch {
  billingType?: BillingType;
  paymentPlan?: PaymentPlan;
  /** Null clears it. */
  paymentMethod?: PaymentMethod | null;
  premiumFinanced?: boolean;
  /** Required exactly when the premium is financed. */
  premiumFinance?: {
    company: string;
    agreementRef: string;
    /** A plain decimal (D-10), never a formatted string. */
    downPayment?: string;
    installments?: number;
  };
}

export interface BindContact {
  name?: string;
  email?: string;
  phone?: string;
}

/** The bind: the elections, and the carrier's bind details. */
export interface BindRequestBody extends BillingElectionPatch {
  /** ISO `YYYY-MM-DD`; the quote's own effective date when absent. */
  requestedEffectiveDate?: string;
  triaElection?: "accepted" | "rejected";
  primaryContact?: BindContact;
  accountingContact?: BindContact;
  /** ISO `YYYY-MM-DD`. */
  applicationSignedOn?: string;
  /** For Signal, at most `BIND_NOTE_MAX`. */
  note?: string;
}

/** The longest note PMS takes with a bind. */
export const BIND_NOTE_MAX = 2000;

/** `failed`: the carrier's bind call failed (PMS-S907). Closed, but never a
 *  decision: no reason, no `decidedAt`, and the quote is back at selected. */
export type BindRequestStatus =
  | "requested"
  | "in_review"
  | "bound"
  | "bound_with_conditions"
  | "declined"
  | "requoted"
  | "withdrawn"
  | "failed";
export const BIND_REQUEST_STATUSES: readonly BindRequestStatus[] = [
  "requested",
  "in_review",
  "bound",
  "bound_with_conditions",
  "declined",
  "requoted",
  "withdrawn",
  "failed",
];

/** The broker's latest bind request on a quote, and Signal's decision. */
export interface BrokerBindRequest {
  id: string;
  status: BindRequestStatus;
  requestedAt: string;
  /** ISO `YYYY-MM-DD`. */
  requestedEffectiveDate: string;
  billing: BillingElection;
  decidedAt: string | null;
  /** Why Signal declined it, written for the broker ("Reason shown to the
   *  broker" in PMS's decline dialog; Nav, 2026-10-27, BRK-S468); null on
   *  every other status. Optional for the shapes that predate it. */
  declineReason?: string | null;
  binderId: string | null;
  /** PMS-S556: a policy issued directly, without a binder. */
  policyId?: string | null;
  /** Why Signal withdrew it, once the broker asked Signal to cancel
   *  (PMS-S759, BRK-S373); null on every other status. Optional while a PMS
   *  before S759 is deployed. */
  withdrawReason?: string | null;
}

/** What a Bind did. `pending` is a carrier that binds by API still binding
 *  it (PMS-S557): an open request, as the quote it returns shows. `failed`
 *  is a replay of a request whose carrier call failed (PMS-S907), the same
 *  failure as its 502. `issued` stays unread: no carrier issues a policy at
 *  bind in the MVP. */
export type BindOutcome =
  | "bind_requested"
  | "pending"
  | "bound"
  | "bound_with_conditions"
  | "declined"
  | "requoted"
  | "withdrawn"
  | "failed";
export const BIND_OUTCOMES: readonly BindOutcome[] = [
  "bind_requested",
  "pending",
  "bound",
  "bound_with_conditions",
  "declined",
  "requoted",
  "withdrawn",
  "failed",
];

/** What a Bind did: 202 while Signal's queue holds it, 200 for a replay of
 *  a request Signal has decided. */
export interface BindResult {
  outcome: BindOutcome;
  quote: BrokerQuote;
}

/** Why PMS won't take a bind, every reason at once (409
 *  `quote_not_bindable`). The codes are in the 409's description, not a
 *  schema, so the contract test holds them to its words. */
export type BindBlockerCode =
  | "not_firm"
  | "not_quotable"
  | "referral_not_approved"
  | "wrong_stage"
  | "quote_expired"
  | "outdated"
  | "backdated"
  | "live_binder_or_policy_exists"
  | "plan_not_offered"
  | "billing_not_elected"
  | "premium_finance_incomplete"
  | "missing_fields";

export type BindBlocker =
  | { code: Exclude<BindBlockerCode, "missing_fields"> }
  | { code: "missing_fields"; fields: string[] };

/** Each blocker as the broker reads it. */
export const BIND_BLOCKER_LABELS: Record<BindBlockerCode, string> = {
  not_firm: "It’s an indication, not a quote.",
  not_quotable: "The carrier declined it.",
  referral_not_approved:
    "The carrier referred it, and its underwriter hasn’t approved it yet.",
  wrong_stage: "Only an offered or selected quote can be bound.",
  quote_expired: "Its validity date has passed.",
  outdated: "It was priced on an earlier version of the application.",
  backdated: "Its effective date has passed.",
  live_binder_or_policy_exists: "It already has a binder or a policy.",
  plan_not_offered: "The payment plan isn’t one the carrier offered.",
  billing_not_elected: "Choose a billing type and a payment plan.",
  premium_finance_incomplete:
    "A financed premium needs the finance company and the agreement.",
  missing_fields: "The application is missing details the bind needs.",
};

const quotePath = (submissionId: string, quoteId: string) =>
  `${BASE}/${encodeURIComponent(submissionId)}/quotes/${encodeURIComponent(quoteId)}`;

/** A 2xx without the quote isn't read as done (NN #7). */
function answered(quote: BrokerQuote | undefined): BrokerQuote {
  if (!quote?.id) {
    throw new ApiError(
      200,
      "Signal took it, but nothing came back to show. Reload to check.",
    );
  }
  return quote;
}

/** POST …/select: choose the quote, or change the elections on it. Another
 *  selected quote goes back to offered. */
export async function selectQuote(
  request: AuthedRequester,
  submissionId: string,
  quoteId: string,
  election: BillingElectionPatch = {},
): Promise<BrokerQuote> {
  return answered(
    await request<BrokerQuote>(`${quotePath(submissionId, quoteId)}/select`, {
      method: "POST",
      body: election,
    }),
  );
}

/** DELETE …/select: back to offered; its elections stay as defaults. */
export async function unselectQuote(
  request: AuthedRequester,
  submissionId: string,
  quoteId: string,
): Promise<BrokerQuote> {
  return answered(
    await request<BrokerQuote>(`${quotePath(submissionId, quoteId)}/select`, {
      method: "DELETE",
    }),
  );
}

/** POST …/bind with the attempt's `Idempotency-Key`: the same key answers
 *  the request it made, so a retry never asks twice. */
export async function requestBind(
  request: AuthedRequester,
  submissionId: string,
  quoteId: string,
  input: { idempotencyKey: string; body: BindRequestBody },
): Promise<BindResult> {
  const result = await request<Partial<BindResult>>(
    `${quotePath(submissionId, quoteId)}/bind`,
    {
      method: "POST",
      body: input.body,
      headers: { "Idempotency-Key": input.idempotencyKey },
    },
  );
  const outcome = result.outcome;
  if (!outcome || !BIND_OUTCOMES.includes(outcome)) {
    throw new ApiError(
      200,
      "Signal answered, but not with where the bind stands. Reload to check.",
    );
  }
  return { outcome, quote: answered(result.quote) };
}

/** A 409 that says where the quote stands instead. */
export type BindConflictCode =
  | "bind_in_progress"
  | "already_bound"
  | "another_quote_chosen"
  | "quote_not_selectable"
  | "quote_expired"
  | "quote_outdated"
  | "elections_frozen"
  | "quote_transition_not_allowed";

export const BIND_CONFLICT_MESSAGES: Record<BindConflictCode, string> = {
  bind_in_progress: "A bind request for this quote is already with Signal.",
  already_bound: "This quote is already bound.",
  another_quote_chosen:
    "Another quote on this submission is already being bound.",
  quote_not_selectable:
    "Only a current quote the carrier will write can be selected.",
  quote_expired: "This quote has expired. Ask the carrier for a new quote.",
  quote_outdated:
    "This quote was priced on an earlier version of the application. Get an updated quote first.",
  elections_frozen:
    "The billing choices can’t change while a bind request is open.",
  quote_transition_not_allowed: "This quote can’t change from where it stands.",
};

/** Each election or date refusal (400), on the field it's about. */
const FIELD_REFUSALS: Record<string, { field: string; message: string }> = {
  // A billing type the carrier doesn't take (PMS-S933, BRK-S510).
  billing_type_not_offered: {
    field: "billingType",
    message: "Choose one of the carrier’s billing types.",
  },
  plan_not_offered: {
    field: "paymentPlan",
    message: "Choose one of the carrier’s payment plans.",
  },
  premium_financed_is_an_election: {
    field: "paymentPlan",
    message:
      "Premium financing is the yes or no under Billing, not a payment plan.",
  },
  premium_finance_required: {
    field: "premiumFinance.company",
    message: "A financed premium needs the finance company and the agreement.",
  },
  premium_finance_not_financed: {
    field: "premiumFinanced",
    message: "Finance terms go with a financed premium only.",
  },
  requested_effective_date_required: {
    field: "requestedEffectiveDate",
    message: "Choose the effective date to bind from.",
  },
  requested_effective_date_in_past: {
    field: "requestedEffectiveDate",
    message: "The effective date can’t be in the past.",
  },
};

export type BindRefusal =
  /** No answer came: the request may or may not have reached Signal. */
  | { kind: "network"; message: string }
  | { kind: "not_bindable"; blockers: BindBlocker[] }
  | { kind: "conflict"; code: BindConflictCode; message: string }
  /** The key named another quote's request: send again with a new one. */
  | { kind: "key_reused" }
  /** The carrier's bind call failed (502 `carrier_error`, PMS-S907): nothing
   *  was declined, and a new request can go. */
  | { kind: "carrier_failed" }
  | { kind: "fields"; byField: Record<string, string>; summary: string }
  | { kind: "refused"; message: string };

/** What a request reads when a fresh key is refused as reused too: the slip
 *  is ours, so PMS's sentence about the key never shows. */
export const KEY_REUSED_MESSAGE =
  "Signal couldn’t take the request. Try again.";

/** A carrier's failed bind call in the portal's words (BRK-S504), as Nav
 *  worded PMS-S907's. PMS's `error` never shows, so none of the carrier's own
 *  words can reach the broker, as with `carrierRefusalLine` (BRK-S496). */
export function bindFailedLine(carrier: string | null | undefined): string {
  const whose = carrier?.trim() ? `${carrier.trim()}’s` : "The carrier’s";
  return `${whose} system couldn’t take the bind request. Try again, or message Signal.`;
}

const CONFLICT_CODES = Object.keys(BIND_CONFLICT_MESSAGES);
const isConflictCode = (code: string | null): code is BindConflictCode =>
  code !== null && CONFLICT_CODES.includes(code);

/** The blockers a 409 lists, as far as they read. */
function blockersOf(err: ApiError): BindBlocker[] {
  const body = err.body;
  const details =
    typeof body === "object" && body !== null
      ? (body as { details?: unknown }).details
      : null;
  const list =
    typeof details === "object" && details !== null
      ? (details as { blockers?: unknown }).blockers
      : null;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item: unknown): BindBlocker[] => {
    if (typeof item !== "object" || item === null) return [];
    const { code, fields } = item as { code?: unknown; fields?: unknown };
    if (code === "missing_fields") {
      return [
        {
          code,
          fields: Array.isArray(fields)
            ? fields.filter((f): f is string => typeof f === "string")
            : [],
        },
      ];
    }
    return typeof code === "string" && code in BIND_BLOCKER_LABELS
      ? [{ code: code as Exclude<BindBlockerCode, "missing_fields"> }]
      : [];
  });
}

/** Why a select, bind or withdraw didn't go through, in words to act on. */
export function bindRefusalOf(err: unknown): BindRefusal {
  if (err instanceof ApiError && err.status === 0) {
    return {
      kind: "network",
      message:
        "We couldn’t confirm your bind request. Check its status before trying again.",
    };
  }
  const code = apiErrorCode(err);
  if (err instanceof ApiError && code === "quote_not_bindable") {
    return { kind: "not_bindable", blockers: blockersOf(err) };
  }
  if (code === "idempotency_key_reused") return { kind: "key_reused" };
  if (code === "carrier_error") return { kind: "carrier_failed" };
  if (isConflictCode(code)) {
    return { kind: "conflict", code, message: BIND_CONFLICT_MESSAGES[code] };
  }
  const onField = code === null ? undefined : FIELD_REFUSALS[code];
  if (onField) {
    return {
      kind: "fields",
      byField: { [onField.field]: onField.message },
      summary: onField.message,
    };
  }
  const saved = readSaveError(err);
  if (saved.kind === "fields") {
    return { kind: "fields", byField: saved.byField, summary: saved.summary };
  }
  return { kind: "refused", message: saved.message };
}

/** Where the broker is with a quote's bind, as its chip and Status say it;
 *  null when it's neither chosen nor asked to bind. "Bound" only on a quote
 *  PMS has at `bound`: a request's own status never says it. */
export function bindStanding(
  quote: BrokerQuote | null | undefined,
): { chip: string; tone: string; line: string } | null {
  if (!quote) return null;
  const request = quote.bindRequest;
  const on = request?.decidedAt
    ? ` on ${formatShortDate(request.decidedAt)}`
    : "";
  if (quote.stage === "bound") {
    return request?.status === "bound_with_conditions"
      ? {
          chip: "Bound with conditions",
          tone: "bound",
          line: `Signal bound this with the carrier${on}, with conditions.`,
        }
      : {
          chip: "Bound",
          tone: "bound",
          line: `Signal bound this with the carrier${on}.`,
        };
  }
  switch (request?.status) {
    case "requested":
      return {
        chip: "Bind requested",
        tone: "waiting_on_carrier",
        line: "Signal binds this with the carrier and confirms it here. This coverage isn’t bound yet.",
      };
    case "in_review":
    case "bound":
    case "bound_with_conditions":
      // Decided as bound, but the quote isn't there yet: still Signal's.
      return {
        chip: "Bind in review",
        tone: "waiting_on_carrier",
        line: "Signal is reviewing your bind request. This coverage isn’t bound yet.",
      };
    case "declined": {
      // The reason Signal wrote for the broker (BRK-S468), as a withdrawal
      // says its own; without one, who to ask.
      const reason = request.declineReason?.trim();
      return {
        chip: "Bind declined",
        tone: "declined",
        line: reason
          ? `Signal declined the bind request${on}: ${reason}`
          : `Signal declined the bind request${on}. Ask Signal why.`,
      };
    }
    case "requoted":
      return {
        chip: "Requoted",
        tone: "draft",
        line: "Signal asked the carrier for a new quote instead of binding this one.",
      };
    case "withdrawn": {
      // Signal withdrew it when the broker asked (PMS-S759, BRK-S373), and
      // says why; the chip still follows the quote's stage.
      const reason = request?.withdrawReason;
      if (!reason) break;
      const line = `Signal withdrew the bind request${on}: ${reason}`;
      return quote.stage === "selected"
        ? { chip: "Selected", tone: "quoted", line }
        : { chip: "Bind withdrawn", tone: "draft", line };
    }
    case "failed":
      // The carrier's call failed (PMS-S907): no decision and no date, and
      // the quote is back at selected for the broker to ask again. Once it
      // moves on, the failure is behind it.
      if (quote.stage !== "selected") break;
      return {
        chip: "Couldn’t send",
        tone: "waiting_on_broker",
        line: bindFailedLine(quote.carrier),
      };
    default:
      break;
  }
  if (quote.stage === "selected") {
    return {
      chip: "Selected",
      tone: "quoted",
      line: "You selected this quote.",
    };
  }
  return null;
}

/** A request Signal hasn't decided, which the broker can ask Signal to
 *  cancel (BRK-S373): only Signal withdraws one (PMS-S759). */
export function isOpenBindRequest(quote: BrokerQuote | null | undefined) {
  const status = quote?.bindRequest?.status;
  return status === "requested" || status === "in_review";
}

/** A quote the broker has chosen or asked to bind: shown first. */
export function isChosen(quote: BrokerQuote | null | undefined): boolean {
  return (
    quote?.stage === "selected" ||
    quote?.stage === "binding" ||
    quote?.stage === "bound"
  );
}

/** Whether the broker can choose it: a current quote the carrier will
 *  write, still valid (`today` as ISO `YYYY-MM-DD`), that's only offered so
 *  far. */
export function canSelect(
  quote: BrokerQuote,
  identity: BrokerIdentity | null,
  today: string,
): boolean {
  return (
    quote.kind === "quote" &&
    quote.bindStatus === "quotable" &&
    !quote.outdated &&
    quote.stage === "offered" &&
    !isExpired(quote, today) &&
    hasCapability(identity, "submission:edit")
  );
}

/** Whether a quote's validity has passed (PMS-S816, BRK-S462): PMS marks it
 *  `expired` in a daily job, and from the day after `quoteValidUntil` it
 *  won't select or bind it, job or not. `today` is the UTC date, as PMS's
 *  is (`utcTodayIso`). An indication is an estimate, whose next step is a
 *  quote anyway, but one from a carrier that prices only when it quotes is
 *  the price to bind (PMS-S750). Once chosen for a bind, or gone, its
 *  validity is behind it. */
export function isExpired(quote: BrokerQuote, today: string): boolean {
  if (quote.kind !== "quote" && !quote.pricesOnlyAtQuote) return false;
  if (quote.stage === "expired") return true;
  if (quote.stage && quote.stage !== "offered" && quote.stage !== "selected")
    return false;
  return quote.quoteValidUntil !== null && quote.quoteValidUntil < today;
}

export function canUnselect(
  quote: BrokerQuote,
  identity: BrokerIdentity | null,
): boolean {
  return (
    quote.stage === "selected" && hasCapability(identity, "submission:edit")
  );
}

/** Whether Bind shows: PMS says the quote can be bound (`bindAction`, which
 *  leaves the elections to the bind view), and the broker may ask. */
export function canBind(
  quote: BrokerQuote,
  identity: BrokerIdentity | null,
): boolean {
  return (
    quote.kind === "quote" &&
    (quote.bindAction ?? "none") !== "none" &&
    (quote.stage === "offered" || quote.stage === "selected") &&
    hasCapability(identity, "quote:bind")
  );
}

/** A plan's election value: its `plan`, or the one its legacy `planType`
 *  names, for a PMS before S553. */
export function planOf(plan: BrokerPaymentPlan): PaymentPlan | null {
  if (plan.plan) return plan.plan;
  switch (plan.planType) {
    case "full":
      return "annual";
    case "semiannual":
      return "semi_annual";
    default:
      return plan.planType;
  }
}
